import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Textarea,
  compressImage,
} from '@structural-climbing/ui'
import {
  type Discipline,
  gradesForDiscipline,
  LEAD_RANK_6A,
  MAX_COMPETITION_ROUTE_LABEL,
} from '@structural-climbing/validation'
import { ROUTE_COLORS, ROUTE_SHEET_PROPS } from './constants'
import { HoldColorPicker } from './HoldColorPicker'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import type { GymSection } from './types'

interface AddRouteModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  apiUrl: string
  /** Sections for the active discipline tab (already filtered). */
  sections: GymSection[]
  /** Active discipline tab; fixes the grade scale and whether a section is required. */
  discipline?: Discipline
  /**
   * Set when the route is being created for a competition. The route is stored
   * competition_only, so it stays off the member Routes page and out of gym
   * standings until the gym converts or archives it after the competition.
   */
  competitionId?: string
  /** The number the form starts on: the next free one in this discipline. */
  suggestedNumber?: string
  /** Numbers already used in this discipline, to warn before a duplicate. */
  takenNumbers?: string[]
}

export function AddRouteModal({
  isOpen,
  onClose,
  onCreated,
  apiUrl,
  sections,
  discipline = 'boulder',
  competitionId,
  suggestedNumber = '',
  takenNumbers = [],
}: AddRouteModalProps) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const gradeConfig = useGymGradeConfig()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Lead walls span 27 French grades, so start the picker at 6a rather than
  // making the setter scroll up from 4. Boulder scales are short; no default.
  const defaultGrade = discipline === 'lead' ? LEAD_RANK_6A : null
  const [grade, setGrade] = useState<number | null>(defaultGrade)
  const [color, setColor] = useState<string>(ROUTE_COLORS[0].value)
  const [sectionId, setSectionId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [number, setNumber] = useState(suggestedNumber)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The modal stays mounted across tab switches; follow the discipline.
  useEffect(() => {
    setGrade(defaultGrade)
  }, [defaultGrade])

  // The suggestion moves on as routes are added; follow it.
  useEffect(() => {
    setNumber(suggestedNumber)
  }, [suggestedNumber])

  function handleClose() {
    setGrade(defaultGrade)
    setColor(ROUTE_COLORS[0].value)
    setSectionId(null)
    setNote('')
    setNumber(suggestedNumber)
    setFile(null)
    setPreview(null)
    setError(null)
    onClose()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null
    setFile(selected)
    if (preview) URL.revokeObjectURL(preview)
    setPreview(selected ? URL.createObjectURL(selected) : null)
  }

  // A competition route is numbered in the competition, not graded: a grade on
  // the wall gives the setters' intent away. It gets one when the gym converts
  // it into a normal route afterwards.
  const graded = !competitionId

  async function handleSubmit() {
    if (graded && !grade) {
      setError(t('routes.gradeRequired'))
      return
    }
    // A lead route must live in a rope section (a no-section route is boulder).
    if (discipline === 'lead' && !sectionId) {
      setError(t('routes.sectionRequired'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const fd = new FormData()
      if (graded) fd.append('grade', String(grade))
      fd.append('color', color)
      fd.append('section_id', sectionId ?? '')
      fd.append('setter_note', note.trim())
      if (competitionId) {
        fd.append('competition_id', competitionId)
        if (number.trim()) fd.append('label', number.trim())
      }
      if (file) {
        const compressed = await compressImage(file, 2 * 1024 * 1024, 2000)
        fd.append('file', compressed)
        const thumbnail = await compressImage(file, 50 * 1024, 200)
        fd.append('thumbnail', thumbnail)
      }

      const res = await fetch(`${apiUrl}/climbing-routes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) {
        const json = (await res.json()) as { error?: string }
        throw new Error(json.error ?? t('routes.errorCreate'))
      }
      onCreated()
      handleClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('routes.errorCreate'))
    } finally {
      setLoading(false)
    }
  }

  // The grade scale is fixed by the active discipline tab; every section shown
  // here belongs to that discipline.
  const gradeOptions = gradesForDiscipline(gradeConfig, discipline)

  // A warning, not a block: two rounds can reuse the same number.
  const numberTaken = number.trim() !== '' && takenNumbers.includes(number.trim())

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
      {...ROUTE_SHEET_PROPS}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{t('routes.addRouteTitle')}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <HoldColorPicker value={color} onChange={setColor} />

                {!graded && (
                  <Input
                    label={t('routes.competitionNumber')}
                    value={number}
                    onValueChange={setNumber}
                    maxLength={MAX_COMPETITION_ROUTE_LABEL}
                    color={numberTaken ? 'warning' : 'default'}
                    description={
                      numberTaken
                        ? t('routes.competitionNumberTaken', { number: number.trim() })
                        : t('routes.competitionNumberHint')
                    }
                  />
                )}

                {graded && (
                  <Select
                    label={t('routes.grade')}
                    placeholder={t('routes.gradePlaceholder')}
                    selectedKeys={grade ? [String(grade)] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0]
                      setGrade(val ? parseInt(String(val), 10) : null)
                    }}
                  >
                    {gradeOptions.map((g) => (
                      <SelectItem key={String(g.rank)} textValue={g.label}>
                        {g.label}
                      </SelectItem>
                    ))}
                  </Select>
                )}

                {sections.length > 0 && (
                  <Select
                    label={t('routes.section')}
                    placeholder={
                      discipline === 'lead' ? t('routes.selectSection') : t('routes.noSection')
                    }
                    isRequired={discipline === 'lead'}
                    selectedKeys={sectionId ? [sectionId] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0]
                      setSectionId(val ? String(val) : null)
                    }}
                  >
                    {sections.map((s) => (
                      <SelectItem key={s.id}>{s.name}</SelectItem>
                    ))}
                  </Select>
                )}

                <Textarea
                  label={t('routes.setterNote')}
                  placeholder={t('routes.setterNotePlaceholder')}
                  value={note}
                  onValueChange={setNote}
                  maxLength={500}
                />

                <div>
                  <p className="mb-1.5 text-sm font-medium">{t('routes.photoLabel')}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    capture="environment"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {preview ? (
                    <div className="relative">
                      <img
                        src={preview}
                        alt="preview"
                        className="h-48 w-full rounded-lg object-cover"
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        className="absolute right-2 top-2"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {t('routes.changePhoto')}
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="flex w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-divider py-8 text-sm text-foreground/50 hover:bg-default-50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>{t('routes.addPhoto')}</span>
                      <span className="text-xs text-foreground/30">{t('routes.photoHint')}</span>
                    </button>
                  )}
                </div>

                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="flat"
                className="flex-1 sm:flex-none"
                onClick={handleClose}
                isDisabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="primary"
                className="flex-1 sm:flex-none"
                onClick={handleSubmit}
                isLoading={loading}
              >
                {t('routes.addRoute')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
